// YNX — VISITORS PANEL (Master Admin only)
// ───────────────────────────────────────────────────────────────
// Real-time analytics dashboard for Carl TACITA.
// Shows visitor stats, conversion funnel, top countries, event breakdown,
// and a paginated visitor list with click-through to detailed timeline.
// ───────────────────────────────────────────────────────────────
import React, { useEffect, useState, useCallback } from 'react';
import {
  Activity, Globe, TrendingUp, MousePointerClick, Eye, UserCheck,
  Sparkles, RefreshCw, Search, X, ChevronRight, Clock, Smartphone,
  Monitor, Trash2,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const FLAGS = {
  FR: '🇫🇷', BE: '🇧🇪', CH: '🇨🇭', CA: '🇨🇦', LU: '🇱🇺', MC: '🇲🇨',
  US: '🇺🇸', GB: '🇬🇧', ES: '🇪🇸', DE: '🇩🇪', IT: '🇮🇹', PT: '🇵🇹',
  NL: '🇳🇱', SE: '🇸🇪', NO: '🇳🇴', DK: '🇩🇰', FI: '🇫🇮', AT: '🇦🇹',
  PL: '🇵🇱', CZ: '🇨🇿', GR: '🇬🇷', IE: '🇮🇪', MA: '🇲🇦', TN: '🇹🇳',
  DZ: '🇩🇿', SN: '🇸🇳', JP: '🇯🇵', AU: '🇦🇺', BR: '🇧🇷', MX: '🇲🇽',
};

const fmtAgo = (iso) => {
  if (!iso) return '';
  try {
    const t = new Date(iso).getTime();
    const diffS = Math.max(0, Math.round((Date.now() - t) / 1000));
    if (diffS < 60)   return `${diffS}s`;
    if (diffS < 3600) return `${Math.round(diffS / 60)}m`;
    if (diffS < 86400) return `${Math.round(diffS / 3600)}h`;
    return `${Math.round(diffS / 86400)}j`;
  } catch (_e) { return ''; }
};

const fmtDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('fr-FR'); } catch (_e) { return iso; }
};

// Detect mobile/desktop from user agent
const deviceOf = (ua) => {
  if (!ua) return 'unknown';
  return /Mobi|Android|iPhone|iPad/i.test(ua) ? 'mobile' : 'desktop';
};

const headers = (secret) => ({
  'Content-Type': 'application/json',
  'X-YNX-Owner-Secret': secret,
});

const apiGet = async (secret, path) => {
  const res = await fetch(`${API}${path}`, { headers: headers(secret) });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
};

export const VisitorsPanel = ({ secret }) => {
  const [stats, setStats]       = useState(null);
  const [visitors, setVisitors] = useState({ total: 0, items: [] });
  const [search, setSearch]     = useState('');
  const [country, setCountry]   = useState('');
  const [event, setEvent]       = useState('');
  const [loading, setLoading]   = useState(true);
  const [selected, setSelected] = useState(null); // visitor detail modal
  const [detail, setDetail]     = useState(null);
  const [err, setErr]           = useState('');

  const refresh = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const qs = new URLSearchParams({ limit: '100', skip: '0' });
      if (country) qs.set('country', country);
      if (event)   qs.set('event', event);
      if (search)  qs.set('search', search);
      const [s, v] = await Promise.all([
        apiGet(secret, '/api/biz/admin/visitors/stats'),
        apiGet(secret, `/api/biz/admin/visitors/list?${qs.toString()}`),
      ]);
      setStats(s);
      setVisitors(v);
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }, [secret, country, event, search]);

  useEffect(() => { refresh(); }, [refresh]);

  // Refresh every 30s while the tab is open
  useEffect(() => {
    const id = setInterval(() => refresh(), 30000);
    return () => clearInterval(id);
  }, [refresh]);

  const openDetail = async (v) => {
    setSelected(v); setDetail(null);
    try {
      const d = await apiGet(secret, `/api/biz/admin/visitors/${v.visitor_id}`);
      setDetail(d);
    } catch (e) { setErr(e.message); }
  };

  const removeVisitor = async (v) => {
    if (!window.confirm(`Supprimer le visiteur ${v.visitor_id.slice(0, 12)}… et tout son historique ? (RGPD)`)) return;
    try {
      await fetch(`${API}/api/biz/admin/visitors/${v.visitor_id}`, {
        method: 'DELETE',
        headers: headers(secret),
      });
      setSelected(null); setDetail(null);
      refresh();
    } catch (e) { setErr(e.message); }
  };

  // Funnel display: compute conversion rates
  const renderFunnel = () => {
    if (!stats?.funnel) return null;
    const f = stats.funnel;
    const base = f.landing || 1;
    const steps = [
      { key: 'landing',    label: 'Page d\'accueil',      count: f.landing,    color: '#94a3b8' },
      { key: 'cta_click',  label: 'Clic sur un CTA',      count: f.cta_click,  color: '#5eead4' },
      { key: 'demo',       label: 'Entrée en démo',       count: f.demo,       color: '#a78bfa' },
      { key: 'onboard',    label: 'Début d\'inscription', count: f.onboard,    color: '#fbbf24' },
      { key: 'signup',     label: 'Compte créé',          count: f.signup,     color: '#16a34a' },
    ];
    return (
      <div className='admin-funnel' data-testid='visitors-funnel'>
        {steps.map((s) => {
          const pct = base ? Math.round((s.count / base) * 100) : 0;
          return (
            <div key={s.key} className='admin-funnel-step'>
              <div className='admin-funnel-label'>
                <span>{s.label}</span>
                <span style={{ color: s.color, fontWeight: 700 }}>
                  {s.count} <small style={{ opacity: 0.6 }}>({pct}%)</small>
                </span>
              </div>
              <div className='admin-funnel-bar'>
                <div className='admin-funnel-fill' style={{ width: `${pct}%`, background: s.color }} />
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <section className='admin-section' data-testid='visitors-panel'>
      {err && <div className='biz-error' style={{ marginBottom: 12 }}>⚠ {err}</div>}

      {/* ─── STATS CARDS ─── */}
      <div className='admin-stat-grid'>
        <div className='admin-stat-card'>
          <div className='admin-stat-icon'><Activity size={18} /></div>
          <div className='admin-stat-num'>{stats?.totals?.visitors_24h ?? '–'}</div>
          <div className='admin-stat-lbl'>visiteurs · 24h</div>
        </div>
        <div className='admin-stat-card'>
          <div className='admin-stat-icon'><Eye size={18} /></div>
          <div className='admin-stat-num'>{stats?.totals?.visitors_7d ?? '–'}</div>
          <div className='admin-stat-lbl'>visiteurs · 7 jours</div>
        </div>
        <div className='admin-stat-card'>
          <div className='admin-stat-icon'><TrendingUp size={18} /></div>
          <div className='admin-stat-num'>{stats?.totals?.visitors_30d ?? '–'}</div>
          <div className='admin-stat-lbl'>visiteurs · 30 jours</div>
        </div>
        <div className='admin-stat-card'>
          <div className='admin-stat-icon'><UserCheck size={18} /></div>
          <div className='admin-stat-num' style={{ color: '#fbbf24' }}>{stats?.totals?.visitors ?? '–'}</div>
          <div className='admin-stat-lbl'>visiteurs · total</div>
        </div>
        <div className='admin-stat-card'>
          <div className='admin-stat-icon'><MousePointerClick size={18} /></div>
          <div className='admin-stat-num'>{stats?.totals?.events_24h ?? '–'}</div>
          <div className='admin-stat-lbl'>événements · 24h</div>
        </div>
        <div className='admin-stat-card'>
          <div className='admin-stat-icon'><Sparkles size={18} /></div>
          <div className='admin-stat-num' style={{ color: '#5eead4' }}>{stats?.funnel?.signup ?? '–'}</div>
          <div className='admin-stat-lbl'>conversions</div>
        </div>
      </div>

      {/* ─── FUNNEL ─── */}
      <div className='admin-block'>
        <div className='admin-block-head'>
          <h3>Entonnoir de conversion</h3>
          <button className='biz-btn-ghost sm' onClick={refresh} disabled={loading}>
            <RefreshCw size={11} /> {loading ? 'Sync…' : 'Actualiser'}
          </button>
        </div>
        {renderFunnel()}
      </div>

      {/* ─── COUNTRIES + EVENTS ─── */}
      <div className='admin-twin-grid'>
        <div className='admin-block'>
          <h3><Globe size={13} style={{ verticalAlign: 'middle' }} /> Pays d'origine (Top 10)</h3>
          {stats?.top_countries?.length > 0 ? (
            <ul className='admin-pill-list'>
              {stats.top_countries.map((c) => (
                <li key={c.country} onClick={() => setCountry(c.country === country ? '' : c.country)}>
                  <span>{FLAGS[c.country] || '🏳️'} {c.country || '??'}</span>
                  <b>{c.count}</b>
                </li>
              ))}
            </ul>
          ) : (
            <div className='biz-empty'>Aucune donnée géographique (le header Cloudflare CF-IPCountry est requis).</div>
          )}
        </div>

        <div className='admin-block'>
          <h3><Activity size={13} style={{ verticalAlign: 'middle' }} /> Événements (Top 20)</h3>
          {stats?.events_breakdown?.length > 0 ? (
            <ul className='admin-pill-list'>
              {stats.events_breakdown.map((e) => (
                <li key={e.event} onClick={() => setEvent(e.event === event ? '' : e.event)}>
                  <span>{e.event}</span>
                  <b>{e.count}</b>
                </li>
              ))}
            </ul>
          ) : (
            <div className='biz-empty'>Aucun événement encore enregistré.</div>
          )}
        </div>
      </div>

      {/* ─── VISITOR LIST ─── */}
      <div className='admin-block'>
        <div className='admin-block-head'>
          <h3>Visiteurs récents ({visitors.total})</h3>
          <div style={{ display: 'flex', gap: 8 }}>
            <div className='admin-search'>
              <Search size={11} />
              <input
                type='text'
                placeholder='Rechercher (referrer, UA, path)…'
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid='visitor-search-input'
              />
              {search && (
                <button onClick={() => setSearch('')}><X size={11} /></button>
              )}
            </div>
            {(country || event) && (
              <button
                className='biz-btn-ghost sm'
                onClick={() => { setCountry(''); setEvent(''); }}
                data-testid='clear-filters-btn'
              >
                Effacer les filtres
              </button>
            )}
          </div>
        </div>

        {(country || event) && (
          <div className='admin-active-filters'>
            {country && <span className='admin-pill active'>{FLAGS[country] || '🏳️'} {country}</span>}
            {event   && <span className='admin-pill active'>↳ {event}</span>}
          </div>
        )}

        {visitors.items.length === 0 && !loading && (
          <div className='biz-empty'>Aucun visiteur correspondant aux critères. Patience, vos premiers prospects arrivent ! 👀</div>
        )}

        {visitors.items.length > 0 && (
          <div className='admin-visitor-list'>
            {visitors.items.map((v) => {
              const device = deviceOf(v.first_user_agent);
              return (
                <button
                  key={v.visitor_id}
                  className='admin-visitor-row'
                  onClick={() => openDetail(v)}
                  data-testid={`visitor-row-${v.visitor_id.slice(0, 6)}`}
                >
                  <div className='admin-visitor-flag'>
                    {FLAGS[v.last_country] || (v.last_country ? '🏳️' : '❓')}
                  </div>
                  <div className='admin-visitor-info'>
                    <div className='admin-visitor-id'>
                      <span style={{ fontFamily: 'monospace' }}>{v.visitor_id.slice(0, 12)}…</span>
                      <span className='admin-visitor-dev'>
                        {device === 'mobile' ? <Smartphone size={10} /> : <Monitor size={10} />}
                        {device}
                      </span>
                      {v.event_types?.includes('signup') && (
                        <span className='admin-visitor-tag converted'>✓ converti</span>
                      )}
                      {v.event_types?.includes('demo_join') && !v.event_types?.includes('signup') && (
                        <span className='admin-visitor-tag demo'>🎬 démo</span>
                      )}
                    </div>
                    <div className='admin-visitor-meta'>
                      <span>{v.events || 1} événements</span>
                      <span>·</span>
                      <span>{(v.last_language || '??').toUpperCase()}</span>
                      <span>·</span>
                      <span>{(v.first_referrer || 'direct').slice(0, 40)}</span>
                    </div>
                  </div>
                  <div className='admin-visitor-time'>
                    <Clock size={10} /> {fmtAgo(v.last_seen)}
                  </div>
                  <ChevronRight size={14} className='admin-visitor-chev' />
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* ─── DETAIL MODAL ─── */}
      {selected && (
        <div className='admin-modal-wrap' onClick={() => setSelected(null)}>
          <div className='admin-modal' onClick={(e) => e.stopPropagation()} data-testid='visitor-detail-modal'>
            <button className='admin-modal-close' onClick={() => setSelected(null)}><X size={16} /></button>
            <h3>
              {FLAGS[selected.last_country] || '🏳️'}
              <span style={{ fontFamily: 'monospace', marginLeft: 8 }}>{selected.visitor_id.slice(0, 16)}…</span>
            </h3>
            <div className='admin-modal-grid'>
              <div><b>Première visite :</b> {fmtDate(selected.first_seen)}</div>
              <div><b>Dernière visite :</b> {fmtDate(selected.last_seen)} ({fmtAgo(selected.last_seen)})</div>
              <div><b>Pays :</b> {selected.last_country || '??'}</div>
              <div><b>Langue :</b> {(selected.last_language || '??').toUpperCase()}</div>
              <div><b>Référent :</b> {selected.first_referrer || 'direct'}</div>
              <div><b>Première page :</b> {selected.first_path || '/'}</div>
              <div style={{ gridColumn: '1 / -1' }}>
                <b>User-Agent :</b><br/>
                <code style={{ fontSize: 10, opacity: 0.7 }}>{selected.first_user_agent || '–'}</code>
              </div>
            </div>

            <h4 style={{ marginTop: 16 }}>Historique ({detail?.events?.length || '…'})</h4>
            <div className='admin-events'>
              {!detail && <div className='biz-empty'>Chargement…</div>}
              {detail?.events?.map((ev) => (
                <div key={ev.id} className='admin-event-row'>
                  <span className='admin-event-time'>{fmtAgo(ev.created_at)}</span>
                  <span className='admin-event-type'>{ev.event_type}</span>
                  <span className='admin-event-path'>{ev.path || '–'}</span>
                  {ev.meta && Object.keys(ev.meta).length > 0 && (
                    <code className='admin-event-meta'>{JSON.stringify(ev.meta).slice(0, 80)}</code>
                  )}
                </div>
              ))}
              {detail?.events?.length === 0 && <div className='biz-empty'>Aucun événement.</div>}
            </div>

            <div className='admin-modal-actions'>
              <button className='biz-btn-ghost' onClick={() => setSelected(null)}>Fermer</button>
              <button
                className='biz-btn-ghost'
                style={{ borderColor: 'rgba(239, 68, 68, 0.5)', color: '#fca5a5' }}
                onClick={() => removeVisitor(selected)}
                data-testid='visitor-delete-btn'
              >
                <Trash2 size={11} /> Supprimer (RGPD)
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};
